import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * AvatarNamePopover — wrap n'importe quel avatar pour afficher son nom
 * complet au CLIC (spec 2026-02-XX rev8, Feb 2026).
 *
 * Objectif : sur desktop le hover révèle le nom via `title`, mais sur
 * tablette/mobile le hover n'existe pas → l'utilisateur ne peut pas savoir
 * qui est l'avatar. Ce wrapper offre un popover Radix qui s'ouvre au
 * clic, se ferme au clic-outside ET s'auto-ferme après 3 secondes.
 *
 * Points-clés :
 *   • ``stopPropagation`` sur le clic du trigger — indispensable dans
 *     TaskListView / Documents où la ligne du tableau navigue vers le
 *     détail. Sans ça, cliquer un avatar déclenche la nav.
 *   • Auto-close 3 s (paramétrable via ``autoCloseMs``, 0 = jamais).
 *   • Le contenu par défaut est ``name`` en bold + ``subtitle`` en
 *     muted (ex: rôle / statut). Accepte aussi ``children`` custom pour
 *     un rendu plus riche.
 *   • Contrôlé en interne via ``open`` state — Radix Popover gère
 *     l'outside-click / Escape nativement.
 *   • ``inline`` (défaut true) rend un ``<span>`` pour ne pas casser la
 *     mise en page inline-flex des piles d'avatars.
 */
export default function AvatarNamePopover({
  name,
  subtitle,
  children,
  autoCloseMs = 3000,
  side = "top",
  align = "center",
  inline = true,
  testid,
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || !autoCloseMs) return undefined;
    const id = setTimeout(() => setOpen(false), autoCloseMs);
    return () => clearTimeout(id);
  }, [open, autoCloseMs]);

  const Wrapper = inline ? "span" : "div";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Wrapper
          role="button"
          tabIndex={0}
          onClick={(e) => {
            // Ne JAMAIS remonter le clic vers la ligne du tableau qui navigue.
            e.stopPropagation();
            e.preventDefault();
            setOpen((v) => !v);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
          className="cursor-pointer outline-none"
          data-testid={testid}
        >
          {children}
        </Wrapper>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={6}
        className="px-3 py-2 w-auto max-w-[280px] bg-[#262626] border-[#3A3A3A] text-white shadow-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid={testid ? `${testid}-popover` : undefined}
      >
        <div className="text-sm font-semibold whitespace-nowrap">{name}</div>
        {subtitle && (
          <div className="text-xs text-muted-foreground mt-0.5 whitespace-nowrap">{subtitle}</div>
        )}
      </PopoverContent>
    </Popover>
  );
}
